<template>
    <div class="ui-page">
        <ui-page-header title="Administradores judiciais"
            subtitle="Base única de administradores judiciais. Toda nomeação aponta para um AJ cadastrado; grafias diferentes viram apelidos do mesmo registro e o CNPJ é a chave.">
            <v-btn v-if="$allowed('prospect.merge')" outlined :disabled="!suggestions.length" @click="mergeOpen = true">
                <v-icon left small>mdi-call-merge</v-icon>Mesclar duplicados<span v-if="suggestions.length">&nbsp;({{ suggestions.length }} {{ suggestions.length === 1 ? "sugestão" : "sugestões" }})</span>
            </v-btn>
            <v-btn v-if="$allowed('create')" color="primary" depressed @click="openEdit(null)"><v-icon left small>mdi-plus</v-icon>Novo AJ</v-btn>
        </ui-page-header>

        <ui-table-card clearable :can-clear="!!search" @clear="search = ''">
            <template #toolbar>
                <v-text-field v-model="search" placeholder="Buscar por nome, grafia ou CNPJ" dense outlined hide-details prepend-inner-icon="mdi-magnify" class="ui-toolbar__search"></v-text-field>
            </template>
            <v-data-table :headers="headers" :items="visible" :loading="loading" disable-sort hide-default-footer :items-per-page="-1" item-key="id"
                no-data-text="Nenhum AJ cadastrado" loading-text="Carregando..." class="ui-clickable-rows" @click:row="openEdit">
                <template v-slot:[`item.legal_name`]="{ item }">
                    <div class="ui-cell-main" style="min-width: 220px;">{{ item.legal_name }}<span v-if="item.is_house" class="ui-muted"> · casa</span></div>
                    <div class="ui-cell-sub">{{ item.document ? formatDocument(item.document) : (item.person_type === "pf" ? "CPF a preencher" : "CNPJ a preencher") }}</div>
                </template>
                <template v-slot:[`item.responsible_lawyer`]="{ item }">
                    <span v-if="item.responsible_lawyer">{{ item.responsible_lawyer }}<span v-if="item.oab" class="ui-muted"> · {{ item.oab }}</span></span>
                    <span v-else class="ui-muted">a preencher</span>
                </template>
                <template v-slot:[`item.hq`]="{ item }">
                    <span>{{ [item.hq_city, item.hq_uf].filter(Boolean).join("/") || "—" }}</span>
                    <div class="ui-cell-sub">{{ item.states.join(", ") || "UFs de atuação a preencher" }}</div>
                </template>
                <template v-slot:[`item.aliases`]="{ item }">
                    <span class="ui-small">{{ otherAliases(item).join(" · ") || "—" }}</span>
                </template>
                <template v-slot:[`item.appointments`]="{ item }">
                    <span class="ui-nowrap">{{ item.appointments.aj }} AJ · {{ item.appointments.pp }} PP</span>
                </template>
                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge v-if="item.is_house" status="active" text="casa" />
                    <ui-badge v-else-if="suggestedIds.has(item.id)" status="pending" text="revisar" />
                    <ui-badge v-else status="active" text="ativo" />
                </template>
            </v-data-table>
        </ui-table-card>
        <p class="ui-small ui-muted mt-2">Campos sugeridos além destes: e-mail, telefone, site e Provimento 231 (cadastro no CNJ). AJ pessoa física entra com CPF e OAB.</p>

        <!-- Cadastro -->
        <v-dialog v-model="edit.open" max-width="680" persistent>
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">{{ edit.id ? "Editar AJ" : "Novo AJ" }}</h3>
                        <p class="ui-section-description">As grafias casam as nomeações lançadas com este cadastro. O primeiro nome é a razão social.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <v-row dense>
                        <v-col cols="12" sm="8"><v-text-field v-model="edit.form.legal_name" label="Razão social / nome" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="4"><v-text-field v-model="edit.form.document" label="CNPJ ou CPF" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="8"><v-text-field v-model="edit.form.responsible_lawyer" label="Advogado(a) responsável" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="4"><v-text-field v-model="edit.form.oab" label="OAB" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="6"><v-text-field v-model="edit.form.hq_city" label="Sede (cidade)" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="2"><v-select v-model="edit.form.hq_uf" :items="ufs" label="UF" outlined dense clearable hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="4"><v-select v-model="edit.form.cnj_prov231" :items="prov231Items" label="Provimento 231 (CNJ)" outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12"><v-select v-model="edit.form.states" :items="ufs" label="UFs de atuação" multiple chips small-chips deletable-chips outlined dense hide-details="auto" :menu-props="{ offsetY: true }"></v-select></v-col>
                        <v-col cols="12" sm="5"><v-text-field v-model="edit.form.email" label="E-mail" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="3"><v-text-field v-model="edit.form.phone" label="Telefone" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12" sm="4"><v-text-field v-model="edit.form.site" label="Site" outlined dense hide-details="auto"></v-text-field></v-col>
                        <v-col cols="12"><v-combobox v-model="edit.form.aliases" label="Outras grafias (apelidos)" multiple chips small-chips deletable-chips outlined dense hide-details="auto"
                            :delimiters="[';']" append-icon="" hint="Enter ou ; para separar"></v-combobox></v-col>
                        <v-col cols="12"><v-textarea v-model="edit.form.notes" label="Observações da equipe" outlined dense rows="2" auto-grow hide-details="auto"></v-textarea></v-col>
                    </v-row>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined :disabled="edit.saving" @click="edit.open = false">Cancelar</v-btn>
                    <v-btn v-if="$allowed('save')" color="primary" depressed :loading="edit.saving" :disabled="!String(edit.form.legal_name || '').trim()" @click="save">Salvar</v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- Mesclagem -->
        <v-dialog v-model="mergeOpen" max-width="720">
            <v-card class="ui-card">
                <div class="ui-card__header">
                    <div>
                        <h3 class="ui-section-title">Mesclar duplicados</h3>
                        <p class="ui-section-description">As nomeações e as grafias do cadastro de origem passam para o de destino; a origem é excluída. Sugestões por CNPJ igual ou nome quase igual.</p>
                    </div>
                </div>
                <div class="ui-card__body">
                    <ui-empty v-if="!suggestions.length" text="Nenhum duplicado provável." />
                    <div v-for="pair in suggestions" :key="`${pair.source.id}-${pair.target.id}`" class="pa-merge">
                        <div class="pa-merge__names">
                            <div><span class="ui-label mb-0">Origem</span>{{ pair.source.legal_name }} <span class="ui-muted">· {{ pair.source.appointments.aj }} AJ · {{ pair.source.appointments.pp }} PP</span></div>
                            <div><span class="ui-label mb-0">Destino</span>{{ pair.target.legal_name }} <span class="ui-muted">· {{ pair.target.appointments.aj }} AJ · {{ pair.target.appointments.pp }} PP</span></div>
                        </div>
                        <div class="pa-merge__actions">
                            <v-btn v-if="$allowed('prospect.merge')" small outlined :disabled="merging" @click="merge(pair.target, pair.source)" :title="'Inverter: manter a origem'"><v-icon small>mdi-swap-vertical</v-icon></v-btn>
                            <v-btn v-if="$allowed('prospect.merge')" small color="primary" depressed :loading="merging" @click="merge(pair.source, pair.target)">Mesclar</v-btn>
                        </div>
                    </div>
                </div>
                <div class="ui-card__footer">
                    <v-btn outlined @click="mergeOpen = false">Fechar</v-btn>
                </div>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from "axios";
import { formatDocument, notifyError, notifySuccess } from "@/components/prospect/prospect.util";

const UFS = ["AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA", "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO"];

function normalize(value) {
    return String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

export default {
    name: "ProspectAdministrators",

    data: () => ({
        items: [],
        suggestions: [],
        loading: false,
        search: "",
        ufs: UFS,
        prov231Items: [{ value: "a_confirmar", text: "A confirmar" }, { value: "sim", text: "Cadastrado" }, { value: "nao", text: "Não cadastrado" }],
        edit: { open: false, saving: false, id: null, form: {} },
        mergeOpen: false,
        merging: false,
        headers: [
            { text: "AJ (razão social)", value: "legal_name" },
            { text: "Advogado(a) responsável · OAB", value: "responsible_lawyer" },
            { text: "Sede · UFs de atuação", value: "hq" },
            { text: "Outras grafias", value: "aliases" },
            { text: "Nomeações", value: "appointments", width: 120 },
            { text: "Situação", value: "status", width: 100 },
        ],
    }),

    computed: {
        visible() {
            const term = normalize(this.search);
            const digits = this.search.replace(/\D/g, "");
            if (!term) return this.items;
            return this.items.filter((item) => [item.legal_name, ...item.aliases].some((name) => normalize(name).includes(term)) || (digits.length >= 5 && String(item.document || "").includes(digits)));
        },
        suggestedIds() {
            return new Set(this.suggestions.flatMap((pair) => [pair.source.id, pair.target.id]));
        },
    },

    created() {
        this.load();
    },

    methods: {
        formatDocument,

        otherAliases(item) {
            return item.aliases.filter((alias) => normalize(alias) !== normalize(item.legal_name));
        },

        async load() {
            this.loading = true;
            try {
                const [list, suggestions] = await Promise.all([
                    axios.get("/api/admin/prospect/administrators", { params: { with_counts: true } }),
                    axios.get("/api/admin/prospect/administrators/merge-suggestions"),
                ]);
                this.items = list.data.content;
                this.suggestions = suggestions.data.content;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.loading = false;
            }
        },

        openEdit(item) {
            this.edit = {
                open: true,
                saving: false,
                id: item ? item.id : null,
                form: item ? {
                    legal_name: item.legal_name,
                    document: item.document ? formatDocument(item.document) : "",
                    responsible_lawyer: item.responsible_lawyer || "",
                    oab: item.oab || "",
                    hq_city: item.hq_city || "",
                    hq_uf: item.hq_uf,
                    states: [...item.states],
                    email: item.email || "",
                    phone: item.phone || "",
                    site: item.site || "",
                    cnj_prov231: item.cnj_prov231 || "a_confirmar",
                    aliases: this.otherAliases(item),
                    notes: item.notes || "",
                } : { legal_name: "", document: "", responsible_lawyer: "", oab: "", hq_city: "", hq_uf: null, states: [], email: "", phone: "", site: "", cnj_prov231: "a_confirmar", aliases: [], notes: "" },
            };
        },

        async save() {
            this.edit.saving = true;
            try {
                const payload = { ...this.edit.form, aliases: (this.edit.form.aliases || []).map((alias) => String(alias).trim()).filter(Boolean) };
                if (this.edit.id) await axios.patch(`/api/admin/prospect/administrators/${this.edit.id}`, payload);
                else await axios.post("/api/admin/prospect/administrators", payload);
                this.edit.open = false;
                notifySuccess(this, this.edit.id ? "Cadastro atualizado." : "AJ cadastrado.");
                await this.load();
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.edit.saving = false;
            }
        },

        async merge(source, target) {
            const confirmed = await this.$dialog.confirm({
                title: "Mesclar cadastros",
                text: `"${source.legal_name}" será unido a "${target.legal_name}". As nomeações e as grafias passam para o destino e a origem é excluída. Confirma?`,
                actions: { false: "Cancelar", true: { text: "Mesclar", color: "primary" } },
            });
            if (!confirmed) return;
            this.merging = true;
            try {
                await axios.post("/api/admin/prospect/administrators/merge", { source_id: source.id, target_id: target.id });
                notifySuccess(this, "Cadastros mesclados.");
                await this.load();
                if (!this.suggestions.length) this.mergeOpen = false;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.merging = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import "../../assets/scss/ui/tokens";

.pa-merge {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid $ui-border-soft;
    font-size: 13px;

    &:last-child {
        border-bottom: 0;
    }
}

.pa-merge__names {
    flex: 1 1 auto;
    display: grid;
    gap: 4px;

    .ui-label {
        display: inline-block;
        width: 64px;
    }
}

.pa-merge__actions {
    display: flex;
    gap: 6px;
}
</style>
